import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Trophy,
  Users,
  MapPin,
  ShoppingBag,
  CalendarDays,
  User as UserIcon,
  Settings,
  Medal,
  Landmark,
} from 'lucide-react';
import listingService, { ListingEntry } from '../../services/listingService';
import communityService from '../../services/communityService';
import postService, { DartPost, getImageUrl as postImageUrl } from '../../services/postService';
import { ImagePlus, MessagesSquare } from 'lucide-react';
import forumService, { ForumTopic } from '../../services/forumService';
import { forumTopicImageUrl } from '../../utils/forumTopicImage';
import ThumbnailImage from '../ThumbnailImage';
import DynamicSlider from '../slider/DynamicSlider';

/** Slider-Location (Web + App an derselben Stelle): Inline-Karte nach der 2. Karte */
export const NEW_ENTRIES_SLIDER_LOCATION = 'new_entries_home';
export const NEW_ENTRIES_SLIDER_INDEX = 2;

const ACCENT = '#3fced1';

type NeuType = 'tournament' | 'club' | 'pub' | 'shop' | 'series' | 'league' | 'association' | 'profile' | 'setup' | 'post' | 'forum';

interface NeuItem {
  key: string;
  title: string;
  type: NeuType;
  imageUrl: string | null;
  overlayUrl: string | null;
  overlayCircle: boolean;
  /** Profil-Avatar: rund, ÜBERLAPPT den Header (nur bei Profil-Karten) */
  avatarUrl: string | null;
  /** Bestleistungen als kompakte Chips (nur bei Profil-Karten) */
  stats: { value: string; label: string }[] | null;
  location: string | null;
  createdAt: number;
  route: string | null;
  /** Beschreibungs-Vorschau unter dem Titel (nullable) */
  description: string | null;
}

/** Beschreibung für die Karten-Vorschau bereinigen (PURE):
 *  HTML-Tags raus, Whitespace kollabieren, leer → null */
export function cleanCardDescription(raw?: string | null): string | null {
  if (!raw) return null;
  const cleaned = raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned.length > 0 ? cleaned : null;
}

/** Bestleistungen → max. 3 kompakte Chips (deutsche Dezimalschreibweise) */
const buildProfileStats = (
  bests?: {
    highestFinish?: number | null;
    bestAverage?: number | null;
    shortestLeg?: number | null;
  } | null,
): { value: string; label: string }[] | null => {
  if (!bests) return null;
  const stats: { value: string; label: string }[] = [];
  if (bests.highestFinish) stats.push({ value: String(bests.highestFinish), label: 'Finish' });
  if (bests.bestAverage) {
    stats.push({ value: bests.bestAverage.toLocaleString('de-DE'), label: 'Ø' });
  }
  if (bests.shortestLeg) stats.push({ value: `${bests.shortestLeg}`, label: 'Darts-Leg' });
  return stats.length > 0 ? stats : null;
};

const TYPE_META: Record<NeuType, { label: string; Icon: typeof Trophy }> = {
  tournament: { label: 'Turnier', Icon: Trophy },
  club: { label: 'Verein', Icon: Users },
  pub: { label: 'Spielstätte', Icon: MapPin },
  shop: { label: 'Dart-Shop', Icon: ShoppingBag },
  series: { label: 'Serie', Icon: CalendarDays },
  league: { label: 'Liga', Icon: Medal },
  association: { label: 'Verband', Icon: Landmark },
  profile: { label: 'Spieler', Icon: UserIcon },
  setup: { label: 'Setup', Icon: Settings },
  post: { label: 'Beitrag', Icon: ImagePlus },
  forum: { label: 'Forum', Icon: MessagesSquare },
};

const timeAgo = (createdAtMs: number): string => {
  if (!createdAtMs) return '';
  const days = Math.floor((Date.now() - createdAtMs) / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'Heute';
  if (days === 1) return 'Vor 1 Tag';
  return `Vor ${days} Tagen`;
};

/**
 * "Neu bei DartCloud" – die neuesten Einträge quer über alle Typen (Turniere,
 * Vereine, Spielstätten, Shops, Serien, Spielerprofile, Dart-Setups) als
 * horizontal scrollbare, kompakte Karten mit Typ-Badge. Datenlogik wie in der
 * Flutter NewEntriesSection; Darstellung im dichten Portal-Layout der
 * Hauptspalte. Blendet sich aus, wenn keine Einträge gefunden werden.
 */
const NeuBeiDartCloudSection: React.FC = () => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<NeuItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const [entries, profilesRes, setupsRes, posts, forumRes] = await Promise.all([
        listingService.getRecentEntries(20).catch(() => [] as ListingEntry[]),
        communityService.getRecentProfiles({ limit: 10, sort: 'newest' }).catch(() => null),
        communityService.getAllSetups({ limit: 10, sort: 'newest' }).catch(() => null),
        // Neueste Beiträge (Marcel 2026-08-31)
        postService.getFeed({ limit: 10 }).catch(() => null),
        // Neueste Forum-Themen inkl. Bild (Marcel 2026-09-03)
        forumService.getTopics({ limit: 10, sort: 'newest' }).catch(() => null),
      ]);

      const listingItems: NeuItem[] = entries.map((e) => ({
        key: `${e.type}-${e.id}`,
        title: e.name,
        type: e.type,
        imageUrl: e.image,
        overlayUrl: e.logo,
        overlayCircle: false,
        avatarUrl: null,
        stats: null,
        location: e.location && e.location !== 'Unbekannt' ? e.location : null,
        createdAt: e.createdAt,
        route: listingService.routeFor(e),
        description: cleanCardDescription(e.description),
      }));

      // Profil-Karten (2026-08-24): Header = headerImage oder grau, das
      // Profilbild ist IMMER der runde Avatar ÜBER dem Header — nie das
      // Karten-Bild selbst. Unten Bestleistungen als Chips.
      const profileItems: NeuItem[] = (profilesRes?.profiles ?? [])
        .filter((p) => p.image)
        .map((p) => ({
          key: `profile-${p.id}`,
          title: p.username,
          type: 'profile' as NeuType,
          imageUrl: p.headerImage
            ? communityService.buildThumbnailUrl(p.headerImage, 400)
            : null,
          overlayUrl: null,
          overlayCircle: false,
          avatarUrl: communityService.buildThumbnailUrl(p.image, 100),
          stats: buildProfileStats(p.personalBests),
          location: null,
          createdAt: (p.memberSince ?? 0) * (p.memberSince && p.memberSince < 1e12 ? 1000 : 1),
          route: `/community/profil/${p.username}`,
          description: cleanCardDescription(p.bio),
        }));

      const setupItems: NeuItem[] = (setupsRes?.setups ?? [])
        .filter((s) => s.image)
        .map((s) => ({
          key: `setup-${s.id}`,
          title: s.title,
          type: 'setup' as NeuType,
          imageUrl: communityService.buildThumbnailUrl(s.image, 400),
          overlayUrl: null,
          overlayCircle: false,
          avatarUrl: null,
          stats: null,
          location: null,
          createdAt: s.createdAt,
          route: `/setup/${s.id}`,
          description: cleanCardDescription(s.description),
        }));

      // Beiträge: erstes Bild als Karte, Autor als runder Avatar
      const postItems: NeuItem[] = (posts?.posts ?? [])
        .filter((p: DartPost) => p.id && p.images.length > 0)
        .map((p: DartPost) => ({
          key: `post-${p.id}`,
          title: p.authorName ?? 'Beitrag',
          type: 'post' as NeuType,
          imageUrl: postImageUrl(p.images[0]),
          overlayUrl: null,
          overlayCircle: false,
          avatarUrl: p.authorImage ? postImageUrl(p.authorImage) : null,
          stats: null,
          location: null,
          createdAt: p.createdAt ?? 0,
          route: `/beitrag/${p.id}`,
          description: cleanCardDescription(p.caption),
        }));

      // Forum-Themen: Header-Bild oder erster Bild-Anhang als Karte, Autor als Avatar
      const forumItems: NeuItem[] = (forumRes?.topics ?? [])
        .filter((t: ForumTopic) => t.slug && t.title)
        .map((t: ForumTopic) => ({
          key: `forum-${t.id ?? t.slug}`,
          title: t.title ?? 'Thema',
          type: 'forum' as NeuType,
          imageUrl: forumTopicImageUrl(t),
          overlayUrl: null,
          overlayCircle: false,
          avatarUrl: forumService.buildAvatarUrl(t.authorAvatarUrl) || null,
          stats: null,
          location: t.categoryName ?? null,
          createdAt: typeof t.createdAt === 'number' ? t.createdAt : (t.createdAt ? new Date(t.createdAt as any).getTime() : 0),
          route: `/forum/topic/${t.slug}`,
          // contentPlain kann selbst ein Quill-Delta (JSON) sein → immer durch extractPlainText
          description: cleanCardDescription(forumService.extractPlainText(t.contentPlain && t.contentPlain.trim() ? t.contentPlain : t.content)),
        }));

      // Mischen: alle Typen zusammen, neueste zuerst
      const merged = [...listingItems, ...profileItems, ...setupItems, ...postItems, ...forumItems].sort(
        (a, b) => b.createdAt - a.createdAt,
      );

      if (!cancelled) {
        setItems(merged.slice(0, 14));
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const skeletons = useMemo(() => Array.from({ length: 5 }), []);

  if (!loading && items.length === 0) return null;

  return (
    <section aria-label="Neu bei DartCloud">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="section-heading text-base font-bold text-white">Neu bei DartCloud</h2>
        {!loading && items.length > 3 && (
          <div className="hidden items-center gap-1.5 sm:flex">
            <button
              type="button"
              aria-label="Zurück"
              onClick={() => scrollBy(-360)}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white"
            >
              <ChevronLeft size={15} />
            </button>
            <button
              type="button"
              aria-label="Weiter"
              onClick={() => scrollBy(360)}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        )}
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {loading
          ? skeletons.map((_, i) => (
              <div
                key={i}
                className="h-[236px] w-[240px] shrink-0 animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900"
              />
            ))
          : items.map((item, index) => (
              <React.Fragment key={item.key}>
                {/* Inline-Slider zwischen 2. und 3. Karte (Marcel 2026-09-15): gleiche
                    Höhe wie die Karten, Breite folgt dem Slide-Format; leer → nichts */}
                {/* Klassen direkt am Slider: ohne Slides rendert er NICHTS – kein
                    leeres Hüll-Element, das den Karten-Abstand verdoppelt (Marcel 2026-09-15) */}
                {index === NEW_ENTRIES_SLIDER_INDEX && (
                  <DynamicSlider location={NEW_ENTRIES_SLIDER_LOCATION} className="hidden h-[248px] w-auto shrink-0 lg:block" />
                )}
                <NeuCard item={item} onClick={() => item.route && navigate(item.route)} />
              </React.Fragment>
            ))}
      </div>
      {/* Mobile: Slider UNTER der Sektion in voller Breite (Marcel 2026-09-15) */}
      {!loading && (
        <DynamicSlider location={NEW_ENTRIES_SLIDER_LOCATION} className="mt-3 w-full lg:hidden" />
      )}
    </section>
  );
};

const NeuCard: React.FC<{ item: NeuItem; onClick: () => void }> = ({ item, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const { label, Icon } = TYPE_META[item.type];
  const showImage = item.imageUrl && !imgError;
  const ago = timeAgo(item.createdAt);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!item.route}
      className="group flex h-[248px] w-[240px] shrink-0 flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 text-left transition-colors hover:border-[#3fced1]/50 disabled:cursor-default"
    >
      {/* Bild + Logo-Overlay — FESTE Höhe wie in der App (h statt aspect,
          sonst streckt der Flex-Row die Karte auf Nachbar-Höhe) */}
      <div className="relative h-[124px] w-full shrink-0 bg-zinc-800">
        <div className="h-full w-full overflow-hidden">
          {showImage ? (
            // 240px-Karte → 400er-Thumbnail statt Original (Fallback: Original)
            <ThumbnailImage
              src={item.imageUrl as string}
              thumbSize={400}
              alt=""
              loading="lazy"
              onError={() => setImgError(true)}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            />
          ) : item.avatarUrl ? (
            /* Profil ohne Header-Bild: dezenter Türkis-Verlauf (App-Akzent) */
            <div
              className="h-full w-full"
              style={{
                background:
                  'linear-gradient(135deg, rgba(63,206,210,0.28) 0%, rgba(63,206,210,0.08) 45%, rgba(39,39,42,0.4) 100%)',
              }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
              <img
                src="/icon.png"
                alt=""
                loading="lazy"
                className="h-12 w-12 rounded-xl shadow-[0_0_28px_rgba(63,206,210,0.25)]"
              />
            </div>
          )}
        </div>

        {/* Overlay (Vereins-Logo) */}
        {item.overlayUrl && (
          <div
            className={`absolute bottom-1.5 right-1.5 h-8 w-8 overflow-hidden border border-zinc-700/60 bg-zinc-900 shadow-sm ${
              item.overlayCircle ? 'rounded-full' : 'rounded-md'
            }`}
          >
            <ThumbnailImage
              src={item.overlayUrl}
              thumbSize={100}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
        )}

        {/* Profil-Avatar: rund, überlappt den Header nach unten */}
        {item.avatarUrl && (
          <div className="absolute -bottom-5 left-3 z-10 h-11 w-11 overflow-hidden rounded-full border-2 border-zinc-900 bg-zinc-800 shadow-md">
            <ThumbnailImage
              src={item.avatarUrl}
              thumbSize={100}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </div>
        )}
      </div>

      {/* Typ-Chip + relative Zeit über dem Titel */}
      <div className={`flex flex-1 flex-col p-2.5 ${item.avatarUrl ? 'pt-7' : ''}`}>
        <div className="mb-2 flex items-center justify-between gap-1.5">
          <span
            className="inline-flex shrink-0 items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold text-black"
            style={{ backgroundColor: ACCENT }}
          >
            <Icon size={11} /> {label}
          </span>
          {ago && <span className="truncate text-[10.5px] text-zinc-500">{ago}</span>}
        </div>
        <h3 className="line-clamp-2 text-[13px] font-semibold leading-snug text-white transition-colors group-hover:text-[#3fced1]">
          {item.title}
        </h3>
        {/* Beschreibungs-Vorschau (Marcel 2026-08-31) */}
        {item.description && (
          <p className="mt-1 line-clamp-2 text-xs leading-snug text-gray-500">
            {item.description}
          </p>
        )}
        {item.location && (
          <div className="mt-auto flex items-center gap-1.5 pt-1.5 text-[11px] text-gray-500">
            <MapPin size={11} className="shrink-0" />
            <span className="truncate">{item.location}</span>
          </div>
        )}
        {/* Bestleistungen (nur Profil-Karten mit hinterlegten Stats) */}
        {item.stats && item.stats.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-1 pt-2">
            {item.stats.map((stat) => (
              <span
                key={stat.label}
                className="rounded-md bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400"
              >
                <span className="font-bold text-white">{stat.value}</span> {stat.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </button>
  );
};

export default NeuBeiDartCloudSection;
